import { useState } from "react";
import type { SnapshotPosition } from "../../lib/api";
import { useCartStore } from "../../lib/cartStore";
import {
	formatAddress,
	formatPercent,
	formatUsdcRaw,
	shortAssetId,
} from "../../lib/format";
import { WeightCell } from "./WeightCell";
import { WithdrawComposer } from "./WithdrawComposer";

export function PositionRow({ position }: { position: SnapshotPosition }) {
	const add = useCartStore((s) => s.add);
	const intents = useCartStore((s) => s.intents);
	const alreadyQueued = intents.some(
		(i) =>
			i.kind === "withdraw" &&
			i.assetId === position.assetId &&
			i.vaultAddress.toLowerCase() === position.vaultAddress.toLowerCase(),
	);
	const [composing, setComposing] = useState(false);

	const empty = BigInt(position.valueUsdcRaw) === 0n;
	const stage = (rawAmount: string) => {
		add({
			kind: "withdraw",
			assetId: position.assetId,
			protocol: position.protocol,
			vaultAddress: position.vaultAddress,
			valueUsdcRaw: rawAmount,
		});
		setComposing(false);
	};

	return (
		<>
			<tr>
				<td className="px-3 py-2 font-mono text-xs text-slate-200">
					{shortAssetId(position.assetId, 26)}
					<div className="text-[10px] text-slate-500">
						{position.protocol} · {formatAddress(position.vaultAddress)}
					</div>
				</td>
				<td className="px-3 py-2 text-right font-mono">
					{formatUsdcRaw(position.valueUsdcRaw)}
				</td>
				<td className="px-3 py-2">
					<WeightCell weight={Number(position.weight)} />
				</td>
				<td className="px-3 py-2 text-right text-emerald-300 font-mono">
					{formatPercent(position.expectedNetApy)}
				</td>
				<td className="px-3 py-2 text-right text-slate-300">
					{position.exitTimeDays}d
				</td>
				<td className="px-3 py-2 text-right">
					<button
						type="button"
						onClick={() => setComposing((v) => !v)}
						disabled={alreadyQueued || empty}
						title={
							alreadyQueued
								? "Already in tx plan"
								: empty
									? "No balance to withdraw"
									: "Stage a withdraw — opens the amount picker"
						}
						className="rounded-md border border-rose-700/50 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-rose-300 transition hover:border-rose-500 hover:text-rose-200 disabled:cursor-not-allowed disabled:opacity-40"
					>
						{alreadyQueued ? "In plan" : composing ? "Cancel" : "Withdraw"}
					</button>
				</td>
			</tr>
			{composing && !alreadyQueued && (
				<WithdrawComposer
					maxValueUsdcRaw={position.valueUsdcRaw}
					onSubmit={stage}
					onCancel={() => setComposing(false)}
				/>
			)}
		</>
	);
}
